import React, { useEffect, useState, useRef } from 'react';
import StoreServices from '../../../services/Store';
import BayServices from '../../../services/Bay';
import SectionServices from '../../../services/Section';
import SaleInvoiceServices from '../../../services/SaleInvoice';
import ReportServices from '../../../services/Report';
import ProductServices from '../../../services/Product';
import { generatePdf } from '../../../utils/helper';

/**
  * Shared Refund/Exchange Form
  * Props:
  * - preselectedInvoices: array of invoice objects (optional)
  * - preselectedItems: array of item objects (optional)
  * - onSubmit: function to call with the built payload
  * - onClose: function to call when closing
  * - isModal: boolean to control rendering mode (default: true for backward compatibility)
  */
const RefundExchangeForm = ({ preselectedInvoices, preselectedItems, onSubmit, onClose = () => { }, isModal = true }) => {
  // State
  const [selectedInvoices, setSelectedInvoices] = useState(preselectedInvoices || []);
  const [selectedItems, setSelectedItems] = useState(preselectedItems || []);
  // itemActions: { [itemId]: { action: 'refund'|'exchange'|null, storeId, bayId, sectionId, quantity, reason, refundMethod, newItemId } }
  const [itemActions, setItemActions] = useState({});

  // Data for dropdowns
  const [stores, setStores] = useState([]);
  const [bays, setBays] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Estimate state for realtime calculations
  const [estimate, setEstimate] = useState({
    refunds: [],
    exchanges: [],
    totals: {
      refundNet: 0,
      exchangeStoreCredit: 0,
      exchangeAmountDue: 0,
    },
  });
  const [estimating, setEstimating] = useState(false);
  const invoiceRef = useRef(null);

  // Invoice search
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceResults, setInvoiceResults] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Fetch stores, bays, sections on mount
  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      StoreServices.getStores({ active: true }),
      BayServices.getBays({ active: true }),
      SectionServices.getSections({ active: true })
    ]).then(([storeRes, bayRes, sectionRes]) => {
      setStores(storeRes.data || storeRes || []);
      setBays(bayRes.data || bayRes || []);
      setSections(sectionRes.data || sectionRes || []);
    }).catch(() => {
      setError('Failed to load store/bay/section data');
    }).finally(() => setLoading(false));
  }, []);

  // Fetch items for preselectedInvoices if needed
  useEffect(() => {
    const needsFetch =
      (preselectedInvoices &&
        (
          !preselectedItems ||
          preselectedItems.length === 0 ||
          preselectedItems.some(item => !item.name)
        )
      );
    if (needsFetch) {
      setLoading(true);
      Promise.all(
        preselectedInvoices.map(inv =>
          SaleInvoiceServices.getSaleInvoiceById(inv.id).then(res => {
            const invoiceData = (res.data && res.data[0]) || res;
            return invoiceData && invoiceData.sale_invoice_items
              ? invoiceData.sale_invoice_items
                .filter(item => (item.quantity || 0) > 0)
                .map(item => ({
                  ...item,
                  availableQuantity: item.quantity || 0,
                  invoiceId: inv.id
                }))
              : [];
          })
        )
      )
        .then(results => {
          setSelectedItems(results.flat());
        })
        .catch(() => setSelectedItems([]))
        .finally(() => setLoading(false));
    } else if (preselectedItems && preselectedItems.length > 0) {
      const filtered = preselectedItems
        .filter(item => (item.quantity - (item.refunded_quantity || 0)) > 0)
        .map(item => ({
          ...item,
          availableQuantity: item.quantity - (item.refunded_quantity || 0),
        }));
      setSelectedItems(filtered);
    }
  }, [preselectedInvoices, preselectedItems]);

  // Handlers
  const handleActionChange = (itemId, field, value) => {
    // Debug
    // eslint-disable-next-line no-console
    console.debug('RefundExchangeForm.handleActionChange', { itemId, field, value });
    setItemActions(prev => {
      const existing = prev[itemId] || {};
      const updated = { ...prev, [itemId]: { ...existing, [field]: value } };
      return updated;
    });
  };

  // Filter helpers
  const getBaysForStore = (storeId) => bays.filter(b => b.store_id === Number(storeId));
  const getSectionsForStoreBay = (storeId, bayId) => sections.filter(s => s.store_id === Number(storeId) && s.bay_id === Number(bayId));

  // Invoice search
  const handleInvoiceSearch = async () => {
    setInvoiceLoading(true);
    setError(null);
    try {
      const res = await SaleInvoiceServices.getSaleInvoices({ search: invoiceSearch, limit: 10 });
      setInvoiceResults(res.data || res || []);
    } catch (e) {
      setError('Failed to search invoices');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleSelectInvoice = async (invoice) => {
    if (selectedInvoices.find(inv => inv.id === invoice.id)) return;
    setSelectedInvoices(prev => [...prev, invoice]);
    try {
      const res = await SaleInvoiceServices.getSaleInvoiceById(invoice.id);
      const invoiceData = (res.data && res.data[0]) || res;
      if (invoiceData && invoiceData.sale_invoice_items) {
        setSelectedItems(prev => ([...prev, ...invoiceData.sale_invoice_items.map(item => ({ ...item, invoiceId: invoice.id }))]));
      }
    } catch {
      setError('Failed to fetch invoice items');
    }
  };

  const handleRemoveInvoice = (invoiceId) => {
    setSelectedInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
    setSelectedItems(prev => prev.filter(item => item.invoiceId !== invoiceId));
    setItemActions(prev => {
      const newActions = { ...prev };
      Object.keys(newActions).forEach(itemId => {
        const item = selectedItems.find(i => i.id === Number(itemId));
        if (item && item.invoiceId === invoiceId) delete newActions[itemId];
      });
      return newActions;
    });
  };
  // Invoice/item selection UI
  const renderInvoiceItemSelection = () => {
    if (preselectedInvoices && preselectedItems) return null;
    return (
      <div style={{ marginBottom: 20 }}>
        <div>
          <input
            type="text"
            placeholder="Search invoices..."
            value={invoiceSearch}
            onChange={e => setInvoiceSearch(e.target.value)}
            style={{ marginRight: 8 }}
          />
          <button onClick={handleInvoiceSearch} disabled={invoiceLoading || !invoiceSearch}>Search</button>
        </div>
        {invoiceLoading && <div>Searching...</div>}
        {invoiceResults.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <b>Results:</b>
            <ul>
              {invoiceResults.map(inv => (
                <li key={inv.id}>
                  #{inv.id} - {inv.order_number || inv.id} - {inv.billing_name} {inv.billing_last_name}
                  <button style={{ marginLeft: 8 }} onClick={() => handleSelectInvoice(inv)}>Select</button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {selectedInvoices.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <b>Selected Invoices:</b>
            <ul>
              {selectedInvoices.map(inv => (
                <li key={inv.id}>
                  #{inv.id} - {inv.order_number || inv.id} - {inv.billing_name} {inv.billing_last_name}
                  <button style={{ marginLeft: 8 }} onClick={() => handleRemoveInvoice(inv.id)}>Remove</button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Build table rows (show item name + variant/attributes together in the Item column)
  const renderTableRows = () => {
    if (!selectedItems.length) return <tr><td colSpan={8}>No items selected.</td></tr>;
    return selectedItems.map(item => {
      const action = itemActions[item.id] || {};
      const baysForStore = action.storeId ? getBaysForStore(action.storeId) : [];
      const sectionsForStoreBay = (action.storeId && action.bayId) ? getSectionsForStoreBay(action.storeId, action.bayId) : [];

      const itemObj = item.item || {};
      const variantObj = item.variant || {};
      const name = itemObj.name || item.name || item.item_name || 'Unnamed Item';
      let attrStr = '';
      if (variantObj.attributes && typeof variantObj.attributes === 'object') {
        attrStr = Object.entries(variantObj.attributes)
          .map(([k, v]) => `${k}: ${v}`)
          .join(' • ');
      } else if (item.variant_options && typeof item.variant_options === 'object') {
        attrStr = Object.entries(item.variant_options)
          .map(([k, vals]) => `${k}: ${Array.isArray(vals) ? vals.join('/') : vals}`)
          .join(' • ');
      }
      const displayName = attrStr ? `${name} — ${attrStr}` : name;

      return (
        <tr key={item.id}>
          <td style={{ padding: '10px 6px', fontWeight: 500, fontSize: 15 }}>{displayName}</td>
          <td>
            <select value={action.action || ''} onChange={e => handleActionChange(item.id, 'action', e.target.value)}>
              <option value=''>None</option>
              <option value='refund'>Refund</option>
              <option value='exchange'>Exchange</option>
            </select>
          </td>
          <td>
            <select value={action.storeId || ''} onChange={e => handleActionChange(item.id, 'storeId', e.target.value)}>
              <option value=''>Select Store</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          </td>
          <td>
            <select value={action.bayId || ''} onChange={e => handleActionChange(item.id, 'bayId', e.target.value)} disabled={!action.storeId}>
              <option value=''>Select Bay</option>
              {baysForStore.map(bay => (
                <option key={bay.id} value={bay.id}>{bay.name}</option>
              ))}
            </select>
          </td>
          <td>
            <select value={action.sectionId || ''} onChange={e => handleActionChange(item.id, 'sectionId', e.target.value)} disabled={!action.storeId || !action.bayId}>
              <option value=''>Select Section</option>
              {sectionsForStoreBay.map(section => (
                <option key={section.id} value={section.id}>{section.name}</option>
              ))}
            </select>
          </td>
          <td>
            <input type='number' min={1} max={item.availableQuantity} value={action.quantity || item.availableQuantity} onChange={e => handleActionChange(item.id, 'quantity', e.target.value)} style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid #ccc' }} />
            {/* <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>
              {item.availableQuantity} available
            </span> */}
          </td>
          <td>
            <input type='text' value={action.reason || ''} onChange={e => handleActionChange(item.id, 'reason', e.target.value)} />
          </td>
          <td>
            {action.action === 'refund' && (
              <select value={action.refundMethod || ''} onChange={e => handleActionChange(item.id, 'refundMethod', e.target.value)}>
                <option value=''>Select Method</option>
                <option value='cash'>Cash</option>
                <option value='credit'>Credit</option>
                <option value='store_credit'>Store Credit</option>
                <option value='original_payment'>Original Payment</option>
              </select>
            )}
            {action.action === 'exchange' && (
              <input type='text' placeholder='New Item ID' value={action.newItemId || ''} onChange={e => handleActionChange(item.id, 'newItemId', e.target.value)} />
            )}
          </td>
        </tr>
      );
    });
  };

  // Submit handler
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const refundMap = {};
      const exchangeMap = {};
      Object.entries(itemActions).forEach(([itemId, action]) => {
        const item = selectedItems.find(i => i.id === Number(itemId));
        if (!item || !action.action) return;
        if (action.action === 'refund') {
          if (!refundMap[item.invoiceId]) refundMap[item.invoiceId] = { items: [], refundMethod: action.refundMethod, reason: action.reason };
          refundMap[item.invoiceId].items.push({
            itemId: item.item_id || item.id,
            saleInvoiceItemId: item.id,
            quantity: Number(action.quantity),
            adjustedPrice: undefined,
            reason: action.reason,
            storeId: Number(action.storeId),
            bayId: Number(action.bayId),
            sectionId: Number(action.sectionId),
          });
          refundMap[item.invoiceId].refundMethod = action.refundMethod;
        } else if (action.action === 'exchange') {
          if (!exchangeMap[item.invoiceId]) exchangeMap[item.invoiceId] = { items: [], notes: action.reason };
          exchangeMap[item.invoiceId].items.push({
            originalItemId: item.item_id,
            variantId: item.variant_id,
            saleInvoiceItemId: item.id,
            newItemId: action.newItemId ? Number(action.newItemId) : undefined,
            newVariantId: action.newVariantId ? Number(action.newVariantId) : undefined,
            quantity: Number(action.quantity),
            storeId: Number(action.storeId),
            bayId: Number(action.bayId),
            sectionId: Number(action.sectionId),
          });
          exchangeMap[item.invoiceId].notes = action.reason;
        }
      });
      const refunds = Object.entries(refundMap).map(([invoiceId, data]) => ({
        transactionId: Number(invoiceId),
        items: data.items,
        refundMethod: data.refundMethod,
        reason: data.reason,
      }));
      const exchanges = Object.entries(exchangeMap).map(([invoiceId, data]) => ({
        originalTransactionId: Number(invoiceId),
        items: data.items,
        notes: data.notes,
      }));
      const payload = { refunds, exchanges };
      const res = await ReportServices.createRefundExchange(payload);
      setSuccess('Refund/Exchange submitted successfully!');
      if (onSubmit) onSubmit(payload, res);
    } catch (e) {
      setError('Failed to submit refund/exchange');
    } finally {
      setSubmitting(false);
    }
  };

  // Print estimate / preview
  const handlePrintEstimate = async () => {
    if (!invoiceRef?.current) return;
    try {
      await generatePdf([invoiceRef], 'refund-estimate');
    } catch (err) {
      console.error('Failed to generate PDF estimate', err);
    }
  };

  // Server-side estimate fetcher (single definition)
  const fetchServerEstimate = async () => {
    setEstimating(true);
    try {
      const ia = itemActions || {};
      const si = selectedItems || [];

      const refundMap = {};
      const exchangeMap = {};
      Object.entries(ia).forEach(([itemId, action]) => {
        const item = si.find(i => i.id === Number(itemId));
        if (!item || !action || !action.action) return;
        if (action.action === 'refund') {
          if (!refundMap[item.invoiceId]) refundMap[item.invoiceId] = { items: [], refundMethod: action.refundMethod, reason: action.reason };
          refundMap[item.invoiceId].items.push({
            itemId: item.item_id || item.id,
            saleInvoiceItemId: item.id,
            quantity: Number(action.quantity || 0),
            adjustedPrice: undefined,
            reason: action.reason,
            storeId: Number(action.storeId || 0),
            bayId: Number(action.bayId || 0),
            sectionId: Number(action.sectionId || 0),
          });
        } else if (action.action === 'exchange') {
          if (!exchangeMap[item.invoiceId]) exchangeMap[item.invoiceId] = { items: [], notes: action.reason };
          exchangeMap[item.invoiceId].items.push({
            originalItemId: item.item_id,
            variantId: item.variant_id,
            saleInvoiceItemId: item.id,
            newItemId: action.newItemId ? Number(action.newItemId) : undefined,
            newVariantId: action.newVariantId ? Number(action.newVariantId) : undefined,
            quantity: Number(action.quantity || 0),
            storeId: Number(action.storeId || 0),
            bayId: Number(action.bayId || 0),
            sectionId: Number(action.sectionId || 0),
          });
        }
      });

      const refunds = Object.entries(refundMap).map(([invoiceId, data]) => ({
        transactionId: Number(invoiceId),
        items: data.items,
        refundMethod: data.refundMethod,
        reason: data.reason,
      }));
      const exchanges = Object.entries(exchangeMap).map(([invoiceId, data]) => ({
        originalTransactionId: Number(invoiceId),
        items: data.items,
        notes: data.notes,
      }));

      const payload = { refunds, exchanges };
      // call server calculate endpoint
      const res = await ReportServices.calculateRefundExchange(payload);
      const data = res?.data ?? res;
      if (data) setEstimate(data);
    } catch (err) {
      console.error('fetchServerEstimate error', err);
      setEstimate({ refunds: [], exchanges: [], totals: { refundNet: 0, exchangeStoreCredit: 0, exchangeAmountDue: 0 } });
    } finally {
      setEstimating(false);
    }
  };

  // Debounced effect to call server estimate
  useEffect(() => {
    const t = setTimeout(() => {
      const ia = itemActions || {};
      const hasAction = Object.values(ia).some(a => a && a.action);
      // eslint-disable-next-line no-console
      console.debug('refund-estimate.effect', { hasAction, iaLength: Object.keys(ia).length, selectedItemsLen: (selectedItems || []).length });
      if (hasAction) {
        fetchServerEstimate();
      } else {
        setEstimate({ refunds: [], exchanges: [], totals: { refundNet: 0, exchangeStoreCredit: 0, exchangeAmountDue: 0 } });
      }
    }, 250);
    return () => clearTimeout(t);
  }, [itemActions, selectedItems, stores, selectedInvoices]);

  // Render component (modal or page)
  const renderContent = () => (
    <div className="card">
      <div className="card-body add-product pb-0" style={{ padding: '1rem' }}>
        {isModal && (
          <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 24, fontSize: 28, background: 'none', border: 'none', cursor: 'pointer', zIndex: 9999, color: '#888' }}>&times;</button>
        )}

        {/* Invoice Information Section - Three Column Layout like Sale Invoice */}
        {preselectedInvoices && preselectedInvoices.length > 0 && (
          <div className="row">
            <div className="col-lg-4 col-sm-6 col-12">
              <div className="mb-2">
                <div className="addproduct-icon list icon" style={{ paddingBottom: '10px', marginBottom: '10px' }}>
                  <h5>
                    <span>Original Invoice Details</span>
                  </h5>
                </div>
                <div className="row">
                  <div className="col-lg-12 col-sm-12 col-12">
                    <div className="mb-1 add-product">
                      <strong>Order Number:</strong><br />
                      {preselectedInvoices[0].order_number || preselectedInvoices[0].id}
                    </div>
                  </div>
                  <div className="col-lg-12 col-sm-12 col-12">
                    <div className="mb-1 add-product">
                      <strong>Order Date:</strong><br />
                      {preselectedInvoices[0].order_date ? new Date(preselectedInvoices[0].order_date).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                  <div className="col-lg-12 col-sm-12 col-12">
                    <div className="mb-1 add-product">
                      <strong>Total Amount:</strong><br />
                      ${parseFloat(preselectedInvoices[0].total_sale || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-sm-6 col-12">
              <div className="mb-2">
                <div className="addproduct-icon list icon cursor-inherit" style={{ paddingBottom: '10px', marginBottom: '10px' }}>
                  <h5>
                    <span>Customer Information</span>
                  </h5>
                </div>
                <div className="row">
                  <div className="col-lg-12 col-sm-12 col-12">
                    <div className="mb-1 add-product">
                      <strong>Customer Name:</strong><br />
                      {preselectedInvoices[0].billing_name} {preselectedInvoices[0].billing_last_name}
                    </div>
                  </div>
                  <div className="col-lg-12 col-sm-12 col-12">
                    <div className="mb-1 add-product">
                      <strong>Phone:</strong><br />
                      {preselectedInvoices[0].billing_phone_number_one}
                    </div>
                  </div>
                  <div className="col-lg-12 col-sm-12 col-12">
                    <div className="mb-1 add-product">
                      <strong>Email:</strong><br />
                      {preselectedInvoices[0].billing_email}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-sm-6 col-12">
              <div className="mb-2">
                <div className="addproduct-icon" style={{ paddingBottom: '10px', marginBottom: '10px' }}>
                  <h5>
                    <span>Delivery Information</span>
                  </h5>
                </div>
                <div className="row">
                  <div className="col-lg-12 col-sm-12 col-12">
                    <div className="mb-1 add-product">
                      <strong>Delivery Date:</strong><br />
                      {preselectedInvoices[0].delivery_date ? new Date(preselectedInvoices[0].delivery_date).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                  <div className="col-lg-12 col-sm-12 col-12">
                    <div className="mb-1 add-product">
                      <strong>Delivery Time:</strong><br />
                      {preselectedInvoices[0].delivery_time_slot?.name || 'N/A'}
                    </div>
                  </div>
                  <div className="col-lg-12 col-sm-12 col-12">
                    <div className="mb-1 add-product">
                      <strong>Status:</strong><br />
                      {preselectedInvoices[0].status || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sale/Refund Section - Like the Sale section in invoice form */}
        <div className="mb-2">
          <div className="addproduct-icon" style={{ paddingBottom: '10px', marginBottom: '10px' }}>
            <h5>Refund & Exchange Items</h5>
          </div>
          {renderInvoiceItemSelection()}
          <div style={{ overflowX: 'auto', flex: 1, minHeight: 240, marginBottom: 28 }}>
            <table border="0" cellPadding="0" style={{ width: '100%', minWidth: 1100, tableLayout: 'fixed', background: '#fff', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#fafbfc', zIndex: 9999 }}>
                <tr>
                  <th style={{ width: 160, padding: '12px 8px', fontWeight: 500, fontSize: 16, borderBottom: '2px solid #e0e0e0' }}>Item</th>
                  <th style={{ width: 120, padding: '12px 8px', fontWeight: 500, fontSize: 16, borderBottom: '2px solid #e0e0e0' }}>Action</th>
                  <th style={{ width: 150, padding: '12px 8px', fontWeight: 500, fontSize: 16, borderBottom: '2px solid #e0e0e0' }}>Store</th>
                  <th style={{ width: 120, padding: '12px 8px', fontWeight: 500, fontSize: 16, borderBottom: '2px solid #e0e0e0' }}>Bay</th>
                  <th style={{ width: 150, padding: '12px 8px', fontWeight: 500, fontSize: 16, borderBottom: '2px solid #e0e0e0' }}>Section</th>
                  <th style={{ width: 100, padding: '12px 8px', fontWeight: 500, fontSize: 16, borderBottom: '2px solid #e0e0e0' }}>Quantity</th>
                  <th style={{ width: 200, padding: '12px 8px', fontWeight: 500, fontSize: 16, borderBottom: '2px solid #e0e0e0' }}>Reason</th>
                  <th style={{ width: 180, padding: '12px 8px', fontWeight: 500, fontSize: 16, borderBottom: '2px solid #e0e0e0' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {selectedItems.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#aaa', fontSize: 18 }}>No items selected.</td></tr>
                ) : renderTableRows()}
              </tbody>
            </table>
          </div>
        </div>

        {/* Global-like loader overlay shown while loading/submitting/estimating */}
        {(loading || submitting || estimating) && (
          <div id="global-loader" className="page-loader" style={{
            position: isModal ? 'absolute' : 'fixed',
            inset: 0,
            zIndex: 9997,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.7)'
          }}>
            <div className="whirly-loader"></div>
          </div>
        )}

        {/* Live Estimate Summary (updates in real time) */}
        <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 180 }}>
              <div style={{ color: '#666', fontSize: 12 }}>Refund Net</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>${Number(estimate.totals.refundNet || 0).toFixed(2)}</div>
            </div>
            <div style={{ minWidth: 180 }}>
              <div style={{ color: '#666', fontSize: 12 }}>Exchange Store Credit</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>${Number(estimate.totals.exchangeStoreCredit || 0).toFixed(2)}</div>
            </div>
            <div style={{ minWidth: 180 }}>
              <div style={{ color: '#666', fontSize: 12 }}>Exchange Amount Due</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>${Number(estimate.totals.exchangeAmountDue || 0).toFixed(2)}</div>
            </div>
          </div>
          <div style={{ color: '#999', fontSize: 12, marginTop: 8 }}>Estimates update automatically as you select items/stores/quantities. Final amounts are calculated when the refund/exchange is processed.</div>
        </div>

        {/* Action buttons */}
        <div className="row mt-3 mb-1">
          <div className="col-lg-12">
            <div className="btn-addproduct mb-4">
              <button onClick={onClose} className="btn btn-cancel me-2">
                {isModal ? 'Cancel' : 'Back to Invoice'}
              </button>
              {/* <button onClick={handlePrintEstimate} className="btn btn-secondary me-2" disabled={estimating}>
                {estimating ? 'Generating...' : 'Print Estimate'}
              </button> */}
              <button onClick={handleSubmit} className="btn btn-submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Refund/Exchange'}
              </button>
            </div>
          </div>
        </div>

        {/* Hidden/preview invoice used for printing estimate */}
        <div style={{ display: 'none' }}>
          <div ref={invoiceRef}>
            <div style={{ width: 950, padding: 24, fontFamily: 'Arial, sans-serif', color: '#222' }}>
              <h2>REFUND / EXCHANGE ESTIMATE</h2>
              {selectedInvoices[0] ? (
                <div>
                  <p><b>Order:</b> {selectedInvoices[0].order_number || selectedInvoices[0].id}</p>
                  <p><b>Date:</b> {selectedInvoices[0].order_date ? new Date(selectedInvoices[0].order_date).toLocaleDateString() : ''}</p>
                  <p><b>Customer:</b> {selectedInvoices[0].billing_name} {selectedInvoices[0].billing_last_name}</p>
                </div>
              ) : null}
              <hr />
              <div>
                <h4>Refund Summary</h4>
                <p>Net Refund: ${Number(estimate.totals.refundNet || 0).toFixed(2)}</p>
              </div>
              <div>
                <h4>Exchange Summary</h4>
                <p>Store Credit: ${Number(estimate.totals.exchangeStoreCredit || 0).toFixed(2)}</p>
                <p>Amount Due (before tax): ${Number(estimate.totals.exchangeAmountDue || 0).toFixed(2)}</p>
              </div>
              <hr />
              <p style={{ fontSize: 12, color: '#666' }}>This is an estimate. Final amounts may vary when processed.</p>
            </div>
          </div>
        </div>

        {success && <div style={{ color: 'green', marginTop: 14, fontSize: 17 }}>{success}</div>}
      </div>
    </div>
  );

  // Render based on modal or page mode
  if (isModal) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.55)',
        zIndex: 9996,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {renderContent()}
      </div>
    );
  }

  return renderContent();
};

export default RefundExchangeForm;